import cn from 'classnames';
import {
  FC,
  KeyboardEvent,
  lazy,
  ReactNode,
  RefObject,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { NoteList } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { DocumentAnswers as NoteAnswers } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';
import { Button, Icon, Tooltip } from '@/shared/ui';

import type {
  DocumentSectionSpec,
  DocumentTemplate,
} from '@/entities/templates';

import { type AnswerEdit, type PendingAnswer } from '@/features/audioRecording';

import type { DocumentHistory } from '../config';
import {
  useKeepScrollPlace,
  useNoteHistory,
  useNoteTeethGroupName,
  useOrganizationNoteLists,
  usePresetHistory,
  useSurfaceHistoryKeys,
  useTemplateIntl,
  type TemplateEdit,
} from '../hooks';
import {
  buildPresetNoteSurface,
  buildViewNoteSurface,
  confirmNoteAssumptions,
  describeNoteLine,
  EMPTY_LINE_PRESETS,
  getNewSectionTitle,
  getTemplateProgress,
  indexNoteLines,
  listBatchConfirmable,
  quoteAttribute,
  readHistoryShortcut,
  revealLinkTarget,
  skipRemainingNoteLines,
  toSavedTemplateSections,
  type NamedNoteLinkOpener,
  type NoteAssumption,
  type NoteOperation,
  type NotePresets,
  type SurfaceLine,
  type TemplateDraft,
  type UnresolvedNoteLine,
} from '../lib';

import { DocumentToolbar } from './DocumentToolbar';
import { DroppedTemplateChanges } from './DroppedTemplateChanges';
import { MissingRequiredSections } from './MissingRequiredSections';
import { NoteAssumptions, type NoteAssumptionsReport } from './NoteAssumptions';
import styles from './NoteDocument.module.scss';
import { NoteDocumentFallback } from './NoteDocumentFallback';
import { NoteSection } from './NoteSection';
import { NoteSetAside } from './NoteSetAside';
import { PartnerNoteCopy } from './PartnerNoteCopy';
import type { TemplateDocumentControls } from './TemplateDocumentEditor';
import { TemplateEditorTools } from './TemplateEditorTools';
import { TemplateSection } from './TemplateSection';

// A block note during and after the recording: scribe's view of it and the
// operations the clinician sends.
export type NoteControls = {
  answers: NoteAnswers | undefined;
  pending: Record<string, PendingAnswer>;
  lists: NoteList[];
  listsFailed?: boolean;
  editable: boolean;
  // Saved to the record: the note reads as text only.
  approved: boolean;
  // The live fill is still adding values.
  listening: boolean;
  // After Stop the lines left to fill are listed for review.
  reviewing: boolean;
  onEdit: (edit: AnswerEdit) => void;
  onUnresolvedChange?: (lines: UnresolvedNoteLine[]) => void;
  onMissingRequiredChange?: (titles: string[]) => void;
  onAssumptionsChange?: (report: NoteAssumptionsReport) => void;
};

// A block template's values, wording and line operations set before the
// recording; they are sent to the note once the encounter exists.
export type PrefillControls = {
  presets: NotePresets;
  lists: NoteList[];
  listsFailed?: boolean;
  onChange: (presets: NotePresets) => void;
};

// An option template's answers: scribe's during and after the recording, or
// the values chosen before the start.
export type OptionAnswerControls = {
  draft: Pick<TemplateDraft, 'answers' | 'aiAnswers' | 'multiSelect'>;
  // Scribe holds the answers: a slot takes another value on request, and a
  // flexible section shows its note.
  live: boolean;
  // Required sections without an answer, pointed out once the recording
  // stopped; they never block finishing.
  missingRequired?: DocumentSectionSpec[];
  onAnswer: (key: string, value: string) => void;
  onRelease?: (key: string) => void;
};

// A block template read on its own, with no note and no values: only its
// structure is edited.
type TemplateViewControls = {
  lists: NoteList[];
  listsFailed?: boolean;
};

type BaseProps = {
  template: DocumentTemplate;
  sourceLabel: ReactNode;
  notice?: ReactNode;
  disabled?: boolean;
  className?: string;
};

// The control that opens the template on the Templates page, the only place
// its structure is edited.
type TemplateLinkProps = {
  templateLink?: ReactNode;
};

// A block template's structure edited in place, offered on the Templates
// page where the clinic may change its templates.
type TemplateEditProps = {
  edit?: TemplateEdit;
  // Shown once the edit mode is left with changes not saved yet.
  saveBar?: ReactNode;
};

type Props = BaseProps &
  (
    | ({
        note: NoteControls;
        prefill?: undefined;
        options?: undefined;
        view?: undefined;
      } & TemplateLinkProps)
    | ({
        prefill: PrefillControls;
        note?: undefined;
        options?: undefined;
        view?: undefined;
      } & TemplateLinkProps)
    | {
        options: OptionAnswerControls;
        note?: undefined;
        prefill?: undefined;
        view?: undefined;
      }
    | ({
        view: TemplateViewControls;
        note?: undefined;
        prefill?: undefined;
        options?: undefined;
      } & TemplateEditProps)
  );

// The note's editor loads with the note it shows, so a page without a block
// note never loads it.
const NoteDocumentEditor = lazy(() =>
  import('./NoteDocumentEditor').then((module) => ({
    default: module.NoteDocumentEditor,
  })),
);

const TemplateDocumentEditor = lazy(() =>
  import('./TemplateDocumentEditor').then((module) => ({
    default: module.TemplateDocumentEditor,
  })),
);

type CopyStatus = 'copied' | 'failed' | null;

const COPY_STATUS_MS = 2500;

// The review list stays short until the clinician asks for every line.
const SHOWN_UNRESOLVED = 5;

// The callback is called from an effect keyed on the reported value only.
const useReport = <T,>(value: T, report: ((value: T) => void) | undefined) => {
  const reportRef = useRef(report);
  reportRef.current = report;
  const key = JSON.stringify(value);

  useEffect(() => {
    reportRef.current?.(JSON.parse(key) as T);
  }, [key]);
};

// A note's section in its editor, or an option template's section card.
const scrollToSection = (root: HTMLElement | null, code: string) => {
  const target = [
    ...(root?.querySelectorAll<HTMLElement>(
      '[data-note-section-code], [data-section-code]',
    ) ?? []),
  ].find(
    (element) =>
      (element.dataset.noteSectionCode ?? element.dataset.sectionCode) === code,
  );
  target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  target?.focus({ preventScroll: true });
};

const scrollToLine = (root: HTMLElement | null, lineId: string) => {
  const line = root?.querySelector<HTMLElement>(
    `[data-note-line-id="${lineId}"]`,
  );
  line?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  line
    ?.querySelector<HTMLElement>(
      '[data-component="workspace-template-block"] button, [data-component="workspace-template-block"] input',
    )
    ?.focus({ preventScroll: true });
};

type FrameProps = {
  template: DocumentTemplate;
  children: ReactNode;
  // A note leaves its template's name and origin to the title above it.
  named?: boolean;
  sourceLabel?: ReactNode;
  // What the document holds, after its origin; its sections by default.
  size?: ReactNode;
  notice?: ReactNode;
  disabled?: boolean;
  dataComponent?: string;
  className?: string;
  rootRef?: RefObject<HTMLDivElement>;
  progressText?: string;
  // A template read without a note has nothing to fill.
  progress?: { filled: number; total: number };
  // What the note still needs, under its head.
  panels?: ReactNode;
  // The one undo and redo of the document, in its toolbar and on
  // Cmd/Ctrl+Z; absent while the document may not be changed.
  history?: DocumentHistory;
  // The document's tools, after its undo and redo.
  toolbar?: ReactNode;
  // The template is edited: the name keeps one line, and the toolbar says so.
  editLabel?: ReactNode;
  editLabelDataComponent?: string;
  // Above the sections and disabled with them, under the toolbar.
  lead?: ReactNode;
};

// What a document shows above its sections: the template's name, origin and
// size, and its one toolbar with how much of it is filled.
export const DocumentFrame: FC<FrameProps> = ({
  template,
  children,
  named = true,
  sourceLabel,
  size,
  notice,
  disabled,
  dataComponent = 'workspace-note-document',
  className,
  rootRef,
  progressText,
  progress,
  panels,
  history,
  toolbar,
  editLabel,
  editLabelDataComponent,
  lead,
}) => {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const action = history && readHistoryShortcut(event);
    if (!action) {
      return;
    }
    event.preventDefault();
    if (action === 'undo') {
      history.undo();
    } else {
      history.redo();
    }
  };

  return (
    <div
      ref={rootRef}
      className={cn(styles.document, className)}
      data-component={dataComponent}
      onKeyDown={onKeyDown}
    >
      <div className={cn(styles.meta, !!editLabel && styles.compactMeta)}>
        {named && (
          <div className={styles.metaText}>
            <span className={cn('p3b', styles.name)}>{template.name}</span>
            <span className={cn('p4', styles.caption)}>
              {sourceLabel && <>{sourceLabel} · </>}
              {size ?? (
                <FormattedMessage
                  id="workspace.template.sectionCount"
                  defaultMessage="{count, plural, one {# section} other {# sections}}"
                  values={{ count: template.sections.length }}
                />
              )}
            </span>
          </div>
        )}
        {notice && <p className={cn('p2', styles.notice)}>{notice}</p>}
        {panels}
      </div>
      {(history || toolbar || editLabel || progress) && (
        <DocumentToolbar
          history={history}
          editLabel={editLabel}
          editLabelDataComponent={editLabelDataComponent}
          progress={progress && { ...progress, label: progressText ?? '' }}
        >
          {toolbar}
        </DocumentToolbar>
      )}
      <fieldset disabled={disabled} className={styles.body}>
        {lead}
        <div className={styles.sections}>{children}</div>
      </fieldset>
    </div>
  );
};

const useLinesProgressText = (progress: { filled: number; total: number }) => {
  const { formatMessage } = useIntl();
  return progress.total > 0
    ? formatMessage(
        {
          id: 'workspace.template.linesProgress',
          defaultMessage:
            'Filled {filled} of {total, plural, one {# line} other {# lines}}',
        },
        { filled: progress.filled, total: progress.total },
      )
    : '';
};

const ListsFailedNotice: FC = () => (
  <p role="status" className={cn('p2', styles.listsFailed)}>
    <FormattedMessage
      id="workspace.note.listsFailed"
      defaultMessage="The clinic's shared lists could not be loaded; their blocks show the stored values only."
    />
  </p>
);

const DroppedChangesPanel: FC<{ edit: TemplateEdit | undefined }> = ({
  edit,
}) =>
  edit && edit.droppedChanges.length > 0 ? (
    <DroppedTemplateChanges
      changes={edit.droppedChanges}
      saved={edit.savedSinceDropped}
      noun="section"
      onHide={edit.hideDroppedChanges}
    />
  ) : null;

type NoteFillProps = BaseProps & TemplateLinkProps & { note: NoteControls };

// A block note from scribe's view: every operation goes to scribe, the note
// keeps one undo, and what holds "Save to record" is listed.
const NoteFill: FC<NoteFillProps> = ({ note, templateLink, ...base }) => {
  const { formatMessage } = useIntl();
  const { template } = base;
  const rootRef = useRef<HTMLDivElement>(null);
  const [shownRevision, setShownRevision] = useState<bigint | null>(null);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [copyStatus, setCopyStatus] = useState<CopyStatus>(null);
  const [allUnresolvedShown, setAllUnresolvedShown] = useState(false);
  const history = useNoteHistory({
    answers: note.answers,
    onEdit: note.onEdit,
  });
  useSurfaceHistoryKeys(
    rootRef,
    note.editable && !base.disabled ? history : undefined,
  );
  const answers = note.answers;
  const surface = answers
    ? buildViewNoteSurface({ template, answers, pending: note.pending })
    : null;
  const editable = note.editable && !base.disabled;

  useReport(surface ? surface.unresolved : null, (lines) =>
    lines ? note.onUnresolvedChange?.(lines) : undefined,
  );
  useReport(
    surface
      ? surface.sections
          .filter((section) => section.missingRequired)
          .map((section) => section.title)
      : null,
    (titles) => (titles ? note.onMissingRequiredChange?.(titles) : undefined),
  );

  useEffect(() => {
    if (answers && shownRevision === null) {
      setShownRevision(answers.Revision);
    }
  }, [answers, shownRevision]);

  useEffect(() => {
    if (!copyStatus) {
      return;
    }
    const timer = window.setTimeout(() => setCopyStatus(null), COPY_STATUS_MS);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  const showLine = (lineId: string) => scrollToLine(rootRef.current, lineId);
  const assumptions = surface?.assumptions ?? [];
  const confirmAssumptions = (list: NoteAssumption[]) => {
    setAcknowledged((current) => [
      ...current,
      ...list.map((assumption) => assumption.lineId),
    ]);
    history.perform(confirmNoteAssumptions(list));
  };
  // The page confirms through the note's latest history, whichever render
  // reported the list.
  const confirmAssumptionsRef = useRef(confirmAssumptions);
  confirmAssumptionsRef.current = confirmAssumptions;
  useReport(
    surface ? { assumptions: surface.assumptions, editable } : null,
    (reported) =>
      reported
        ? note.onAssumptionsChange?.({
            assumptions: reported.assumptions,
            confirmAll: reported.editable
              ? () =>
                  confirmAssumptionsRef.current(
                    listBatchConfirmable(reported.assumptions),
                  )
              : undefined,
            confirm: reported.editable
              ? (assumption) => confirmAssumptionsRef.current([assumption])
              : undefined,
            showLine,
          })
        : undefined,
  );

  // The clipboard is missing outside secure contexts.
  const copy = (text: string) =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(() => setCopyStatus('copied'))
      .catch(() => setCopyStatus('failed'));

  const unresolvedCount = answers?.View?.UnresolvedLineCount ?? 0;
  // A partner template's open lines keep its defaults and hold nothing.
  const partnerDefaults = !!answers?.View?.PartnerDefaults;
  const holdingCount = partnerDefaults ? 0 : unresolvedCount;
  const assumptionCount = answers?.View?.UnconfirmedAssumptionCount ?? 0;
  const editsPending = Object.keys(note.pending).length > 0;
  const assumptionsHint = formatMessage({
    id: 'workspace.note.copyBlockedAssumptions',
    defaultMessage:
      'Confirm or change the values AI assumed or suggested first',
  });
  const copyBlocked =
    !surface || surface.ready
      ? null
      : !holdingCount && assumptionCount > 0
        ? assumptionsHint
        : formatMessage({
            id: 'workspace.note.copyBlocked',
            defaultMessage: 'Fill or skip every line first',
          });
  // The editor reads every line's mark again when this changes.
  const isNew = useCallback(
    (line: SurfaceLine) =>
      shownRevision !== null &&
      line.revealedAt > shownRevision &&
      !acknowledged.includes(line.id),
    [shownRevision, acknowledged],
  );
  const missing =
    note.reviewing && surface
      ? surface.sections
          .filter((section) => section.missingRequired)
          .map(({ code, title }) => ({ code, title }))
      : [];
  const unresolved = surface?.unresolved ?? [];
  const partnerSource = template.partner_source;
  const progress = surface?.progress ?? { filled: 0, total: 0 };
  const progressText = useLinesProgressText(progress);
  const perform = (operation: NoteOperation, undoable?: boolean) => {
    setAcknowledged((current) => [...current, ...operation.lineIds]);
    history.perform(operation, undoable);
  };
  const panels = (
    <>
      {missing.length > 0 && (
        <MissingRequiredSections
          className={styles.panel}
          sections={missing}
          onSelect={(code) => scrollToSection(rootRef.current, code)}
        />
      )}
      {note.reviewing && surface && !surface.ready && unresolved.length > 0 && (
        <div
          role="status"
          className={cn('p2', styles.unresolvedPanel)}
          data-component="workspace-note-unresolved"
        >
          <p className={styles.unresolvedTitle}>
            <FormattedMessage
              id="workspace.note.unresolvedTitle"
              defaultMessage="To save the note to the record, fill or skip these lines:"
            />
          </p>
          <ul className={styles.unresolvedList}>
            {(allUnresolvedShown
              ? unresolved
              : unresolved.slice(0, SHOWN_UNRESOLVED)
            ).map((line) => (
              <li key={line.lineId}>
                <button
                  type="button"
                  className={styles.unresolvedLink}
                  onClick={() => showLine(line.lineId)}
                >
                  {line.sectionTitle}: {line.label}
                </button>
              </li>
            ))}
          </ul>
          {unresolved.length > SHOWN_UNRESOLVED && (
            <button
              type="button"
              className={styles.unresolvedLink}
              aria-expanded={allUnresolvedShown}
              onClick={() => setAllUnresolvedShown((shown) => !shown)}
            >
              {allUnresolvedShown ? (
                <FormattedMessage
                  id="workspace.note.unresolvedShowFewer"
                  defaultMessage="Show fewer"
                />
              ) : (
                <FormattedMessage
                  id="workspace.note.unresolvedShowAll"
                  defaultMessage="Show all {count}"
                  values={{ count: unresolved.length }}
                />
              )}
            </button>
          )}
        </div>
      )}
      {note.reviewing && assumptions.length > 0 && (
        <NoteAssumptions
          className={styles.panel}
          assumptions={assumptions}
          onSelect={showLine}
          onConfirmAll={
            editable
              ? () => confirmAssumptions(listBatchConfirmable(assumptions))
              : undefined
          }
          onConfirm={
            editable
              ? (assumption) => confirmAssumptions([assumption])
              : undefined
          }
        />
      )}
      {note.listsFailed && <ListsFailedNotice />}
    </>
  );

  const toolbar = surface && answers && (
    <>
      <p role="status" className={cn('p2', styles.noteStatus)}>
        {surface.ready && partnerDefaults && unresolvedCount > 0 ? (
          <FormattedMessage
            id="workspace.note.partnerReady"
            defaultMessage="Ready to save. Lines not filled keep the template's defaults."
          />
        ) : surface.ready ? (
          <FormattedMessage
            id="workspace.note.ready"
            defaultMessage="Every line is filled or skipped."
          />
        ) : (
          assumptionCount > 0 && (
            <FormattedMessage
              id="workspace.note.assumptionsLeft"
              defaultMessage="{count, plural, one {# AI value to confirm} other {# AI values to confirm}}"
              values={{ count: assumptionCount }}
            />
          )
        )}
      </p>
      {note.reviewing &&
        editable &&
        surface.sections.some(
          (section) => section.unresolvedLineIds.length,
        ) && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => perform(skipRemainingNoteLines(surface.sections))}
          >
            <FormattedMessage
              id="workspace.note.skipRemaining"
              defaultMessage="Skip remaining lines"
            />
          </Button>
        )}
      {editable && (
        <NoteSetAside sections={surface.sections} onOperation={perform} />
      )}
      <span className={styles.spacer} />
      {/* The server withdraws the approval with the first change after it. */}
      {note.approved && (
        <span
          role="status"
          className={cn('p4', styles.savedMark)}
          data-component="workspace-note-saved-mark"
        >
          <Icon className={styles.savedIcon} name="check" size={14} />
          <FormattedMessage
            id="workspace.note.savedToRecord"
            defaultMessage="Saved to record"
          />
        </span>
      )}
      <Button
        variant="secondary"
        size="small"
        icon="copy"
        disabled={!!copyBlocked}
        title={copyBlocked ?? undefined}
        onClick={() =>
          void copy(
            surface.sections
              .filter((section) => section.text.trim())
              .map((section) => `${section.title}\n${section.text}`)
              .join('\n\n'),
          )
        }
      >
        <FormattedMessage
          id="workspace.note.copyNote"
          defaultMessage="Copy note"
        />
      </Button>
      {partnerSource && (
        <PartnerNoteCopy
          partner={partnerSource.partner}
          encounterId={answers.EncounterID}
          documentId={answers.DocumentID}
          revision={Number(answers.Revision)}
          defaultedLineCount={unresolvedCount}
          listening={note.listening}
          editsPending={editsPending}
          blockedHint={assumptionCount > 0 ? assumptionsHint : undefined}
          describeLine={(lineId) =>
            describeNoteLine(
              indexNoteLines(template, answers).get(lineId)?.line,
            )
          }
          onShowLine={showLine}
        />
      )}
      {templateLink}
      {copyStatus && (
        <span
          role="status"
          className={cn(
            'p4',
            styles.caption,
            copyStatus === 'failed' && styles.copyFailed,
          )}
        >
          {copyStatus === 'copied' ? (
            <FormattedMessage
              id="workspace.note.copied"
              defaultMessage="Copied"
            />
          ) : (
            <FormattedMessage
              id="workspace.note.copyFailed"
              defaultMessage="Could not copy. Select the text in the document instead."
            />
          )}
        </span>
      )}
    </>
  );

  return (
    <DocumentFrame
      {...base}
      named={false}
      rootRef={rootRef}
      progressText={progressText}
      progress={progress}
      panels={panels}
      history={editable ? history : undefined}
      toolbar={toolbar}
    >
      {surface ? (
        <Suspense fallback={<NoteDocumentFallback surface={surface} />}>
          <NoteDocumentEditor
            surface={surface}
            mode="note"
            lists={note.lists}
            notation={answers?.Context?.Notation}
            language={template.language}
            editable={editable}
            listening={note.listening}
            // An approved note may still change, and a change drops the
            // approval; only a note that cannot change reads as text.
            approved={note.approved && !editable}
            ariaLabel={template.name}
            history={editable ? history : undefined}
            isNew={isNew}
            onOperation={perform}
          />
        </Suspense>
      ) : (
        <p className={cn('p3', styles.loading)}>
          <FormattedMessage id="global.loading" defaultMessage="Loading…" />
        </p>
      )}
    </DocumentFrame>
  );
};

type PrefillFillProps = BaseProps &
  TemplateLinkProps & { prefill: PrefillControls };

// A block template before the recording: the values known already are
// chosen on its lines, and its wording and lines are changed as on the note,
// with one undo.
const PrefillFill: FC<PrefillFillProps> = ({
  prefill,
  templateLink,
  ...base
}) => {
  const { template } = base;
  const rootRef = useRef<HTMLDivElement>(null);
  const history = usePresetHistory({
    template,
    presets: prefill.presets,
    onChange: prefill.onChange,
  });
  useSurfaceHistoryKeys(rootRef, base.disabled ? undefined : history);
  const teethGroupName = useNoteTeethGroupName(template.language);
  const surface = buildPresetNoteSurface({
    template,
    presets: prefill.presets.blocks,
    lines: prefill.presets.lines,
    filter: 'hideLinked',
    teethGroupName,
  });
  const progressText = useLinesProgressText(surface.progress);

  return (
    <DocumentFrame
      {...base}
      named={false}
      progressText={progressText}
      progress={surface.progress}
      rootRef={rootRef}
      history={base.disabled ? undefined : history}
      panels={prefill.listsFailed && <ListsFailedNotice />}
      toolbar={
        (!base.disabled || templateLink) && (
          <>
            {!base.disabled && (
              <NoteSetAside
                sections={surface.sections}
                onOperation={history.perform}
              />
            )}
            <span className={styles.spacer} />
            {templateLink}
          </>
        )
      }
    >
      <Suspense fallback={<NoteDocumentFallback surface={surface} />}>
        <NoteDocumentEditor
          surface={surface}
          mode="prefill"
          lists={prefill.lists}
          language={template.language}
          editable={!base.disabled}
          listening={false}
          ariaLabel={template.name}
          history={base.disabled ? undefined : history}
          onOperation={history.perform}
        />
      </Suspense>
    </DocumentFrame>
  );
};

type TemplateViewProps = BaseProps &
  TemplateEditProps & { view: TemplateViewControls };

// A block template as the note reads before anything is chosen, with no
// values to choose: its structure is changed in the edit mode only.
const TemplateView: FC<TemplateViewProps> = ({
  view,
  edit,
  saveBar,
  ...base
}) => {
  const { template } = base;
  const rootRef = useRef<HTMLDivElement>(null);
  const teethGroupName = useNoteTeethGroupName(template.language);
  // A template the clinic may change opens in the edit mode; it is read here
  // only while its changes wait to be saved or dropped.
  const opensEditing =
    !!edit && !edit.editing && !edit.changed && !base.disabled;

  useEffect(() => {
    if (opensEditing) {
      edit.start();
    }
    // The edit opens once each time the template is shown without one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opensEditing]);

  if (edit?.editing && edit.draft) {
    return (
      <TemplateEditFill
        // Another source, as after a reload, is another document.
        key={edit.source?.id}
        {...base}
        edit={edit}
        lists={view.lists}
      />
    );
  }

  // Every line shows, the linked ones with what opens them.
  const surface = buildPresetNoteSurface({
    template,
    presets: {},
    teethGroupName,
  });
  // The option a linked line hangs on may sit in another section, so it is
  // looked for in the whole document.
  const showOpener = ({ blockId, lineId, value }: NamedNoteLinkOpener) => {
    const line = rootRef.current?.querySelector<HTMLElement>(
      `[data-line-id="${quoteAttribute(lineId)}"]`,
    );
    const block = line?.querySelector<HTMLElement>(
      `[data-block-id="${quoteAttribute(blockId)}"]`,
    );
    revealLinkTarget([
      line,
      block?.querySelector<HTMLElement>(
        `[data-option-value="${quoteAttribute(value)}"]`,
      ) ?? block,
    ]);
  };
  return (
    <DocumentFrame
      {...base}
      rootRef={rootRef}
      panels={
        <>
          <DroppedChangesPanel edit={edit} />
          {edit?.changed && saveBar}
          {view.listsFailed && <ListsFailedNotice />}
        </>
      }
    >
      {surface.sections.map((section) => (
        <NoteSection
          key={section.id}
          section={template.sections.find((item) => item.ID === section.id)}
          template={template}
          surface={section}
          lists={view.lists}
          language={template.language}
          onShowOpener={showOpener}
        />
      ))}
    </DocumentFrame>
  );
};

type EditFillProps = BaseProps & {
  edit: TemplateEdit;
  // The lists the template reads.
  lists: NoteList[];
};

// The template in the edit mode: one document of the whole template, typed
// as text, with a toolbar for headings, AI instructions and fields, and the
// settings of a section or field in a pop-up above it; saved as a clinic
// version afterwards.
const TemplateEditFill: FC<EditFillProps> = ({
  edit,
  lists: noteLists,
  ...base
}) => {
  const { formatMessage } = useIntl();
  const template = edit.draft?.template ?? base.template;
  // The document reads the draft once; it writes every change back.
  const [initial] = useState(template);
  const templateIntl = useTemplateIntl(template.language);
  const teethGroupName = useNoteTeethGroupName(template.language);
  const clinicLists = useOrganizationNoteLists(template.language);
  const [controls, setControls] = useState<TemplateDocumentControls>();
  // The template as the recording page shows it, with values chosen there
  // only to try it; the document stays as it is, its undo with it.
  const [previewing, setPreviewing] = useState(false);
  const [previewPresets, setPreviewPresets] = useState<NotePresets>({
    blocks: {},
    lines: EMPTY_LINE_PRESETS,
  });
  // The view on screen, the editor or the preview.
  const viewRef = useRef<HTMLDivElement>(null);
  const keepScrollPlace = useKeepScrollPlace(previewing, viewRef);

  const lists = [...clinicLists.lists, ...noteLists].filter(
    (list, index, all) => all.findIndex(({ ID }) => ID === list.ID) === index,
  );
  const newSectionTitle = getNewSectionTitle(
    template,
    templateIntl.formatMessage({
      id: 'workspace.template.newSection',
      defaultMessage: 'New section',
    }),
  );

  const switchView = (preview: boolean) => {
    keepScrollPlace();
    setPreviewing(preview);
  };

  return (
    <>
      {previewing && (
        <div ref={viewRef} className={styles.viewHold}>
          <PrefillFill
            {...base}
            template={{
              ...template,
              sections: toSavedTemplateSections(template),
            }}
            prefill={{
              presets: previewPresets,
              lists,
              listsFailed: clinicLists.failed,
              onChange: setPreviewPresets,
            }}
            templateLink={
              <Button
                variant="secondary"
                size="small"
                icon="pen"
                data-component="workspace-template-preview-back"
                onClick={() => switchView(false)}
              >
                <FormattedMessage
                  id="workspace.templateEdit.backToEditor"
                  defaultMessage="Back to editing"
                />
              </Button>
            }
          />
        </div>
      )}
      <div
        ref={previewing ? undefined : viewRef}
        className={styles.viewHold}
        hidden={previewing}
      >
        <DocumentFrame
          {...base}
          template={template}
          panels={<DroppedChangesPanel edit={edit} />}
          history={controls}
          toolbar={
            <>
              {!base.disabled && <TemplateEditorTools controls={controls} />}
              <Button
                className={styles.toolbarEnd}
                variant="tertiary"
                size="small"
                icon="eye"
                data-component="workspace-template-preview"
                onClick={() => switchView(true)}
              >
                <FormattedMessage
                  id="workspace.templateEdit.preview"
                  defaultMessage="Preview"
                />
              </Button>
              <Tooltip.Primary
                content={formatMessage({
                  id: 'workspace.templateEdit.banner',
                  defaultMessage:
                    'Editing the template — saved as a new clinic version',
                })}
              >
                <Button
                  variant="primary"
                  size="small"
                  // Nothing changed is nothing to save.
                  disabled={!edit.changed}
                  data-component="workspace-template-edit-done"
                  onClick={edit.finish}
                >
                  <FormattedMessage id="global.done" defaultMessage="Done" />
                </Button>
              </Tooltip.Primary>
            </>
          }
        >
          <Suspense
            fallback={
              <NoteDocumentFallback
                surface={buildPresetNoteSurface({
                  template,
                  presets: {},
                  teethGroupName,
                })}
              />
            }
          >
            <TemplateDocumentEditor
              template={initial}
              source={edit.source ?? base.template}
              lists={lists}
              issues={edit.issues}
              editable={!base.disabled}
              focus={edit.focus}
              ariaLabel={template.name}
              newSectionTitle={newSectionTitle}
              newFieldName={templateIntl.formatMessage({
                id: 'global.name',
                defaultMessage: 'Name',
              })}
              onChange={edit.change}
              onControls={setControls}
            />
          </Suspense>
        </DocumentFrame>
      </div>
    </>
  );
};

type OptionFillProps = BaseProps & { options: OptionAnswerControls };

// An option template's fields with their answers; the template itself is
// not edited here.
const OptionFill: FC<OptionFillProps> = ({ options, ...base }) => {
  const { formatMessage } = useIntl();
  const { template } = base;
  const rootRef = useRef<HTMLDivElement>(null);
  // Live answers carry no multi-select flag: a slot is single until the
  // clinician asks for another value or the server already holds several.
  const [liveMultiSelect, setLiveMultiSelect] = useState<Record<string, true>>(
    {},
  );
  const draft: TemplateDraft = { template, ...options.draft };
  const multiSelect = { ...options.draft.multiSelect, ...liveMultiSelect };
  const missing = (options.missingRequired ?? []).map(({ Code, Title }) => ({
    code: Code,
    title: Title,
  }));
  const progress = getTemplateProgress(draft);
  const progressText = [
    progress.total > 0 &&
      formatMessage(
        {
          id: 'workspace.template.requiredProgress',
          defaultMessage: 'Required sections: {filled} of {total}',
        },
        { filled: progress.filled, total: progress.total },
      ),
    progress.optionalTotal > 0 &&
      formatMessage(
        {
          id: 'workspace.template.optionalProgress',
          defaultMessage: 'Optional sections: {filled} of {total}',
        },
        { filled: progress.optionalFilled, total: progress.optionalTotal },
      ),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <DocumentFrame
      {...base}
      named={false}
      rootRef={rootRef}
      progressText={progressText}
      // Without required sections the bar follows the optional ones.
      progress={
        progress.total
          ? { filled: progress.filled, total: progress.total }
          : { filled: progress.optionalFilled, total: progress.optionalTotal }
      }
      panels={
        missing.length > 0 && (
          <MissingRequiredSections
            className={styles.panel}
            sections={missing}
            onSelect={(code) => scrollToSection(rootRef.current, code)}
          />
        )
      }
    >
      {template.sections.map((section) => (
        <TemplateSection
          key={section.Code}
          section={section}
          answers={draft}
          multiSelect={multiSelect}
          showInstructions={false}
          noteMode={
            options.live ? (base.disabled ? 'view' : 'edit') : undefined
          }
          onAnswer={options.onAnswer}
          onRelease={options.onRelease}
          onAllowMultiple={
            options.live && !base.disabled
              ? (key) =>
                  setLiveMultiSelect((current) => ({ ...current, [key]: true }))
              : undefined
          }
        />
      ))}
    </DocumentFrame>
  );
};

// The note as the clinician fills it, before, during and after the
// recording: it reads as a document, with open places only where something
// is still undecided. Its template's structure is edited only with `view`, on
// the Templates page, and saved as a clinic version through `saveBar`; a note
// opens that page with `templateLink`. A partner template's structure comes
// from the partner's template, so it is neither edited nor linked.
export const NoteDocument: FC<Props> = (props) => {
  const partner = !!props.template.partner_source;
  if (props.note) {
    return (
      <NoteFill
        {...props}
        note={props.note}
        templateLink={!partner && props.templateLink}
      />
    );
  }
  if (props.prefill) {
    return (
      <PrefillFill
        {...props}
        prefill={props.prefill}
        templateLink={!partner && props.templateLink}
      />
    );
  }
  if (props.view) {
    return (
      <TemplateView
        {...props}
        view={props.view}
        edit={partner ? undefined : props.edit}
      />
    );
  }
  return props.options ? (
    <OptionFill {...props} options={props.options} />
  ) : null;
};
